import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PersonaTipoUtenteEnum } from '../../../../api/generated/apc/api';
import {
  getStatusRegistrazione,
  insertEmail,
  insertFiscalCodeLegalPerson,
  insertPhoneNumber,
  insertSecondaryPhoneNumber,
  prepareRegistration,
  privacyAgreement,
  retrieveSignatureDocument,
  setupAccount,
  verificationEmail,
  verificationPhoneNumber,
} from './user-registration-thunk';
import {
  SetupAccountError,
  UserRegistrationState,
} from './user-registration-types';

const initialState: UserRegistrationState = {
  flowStarted: false,
  getStatusRegistrazioneRequestStatus: 'idle',
  setupAccountRequestStatus: 'idle',
  selectedOptionUserType: 'PF',
  userCodeUnchanged: '',
  userBehalfOfCode: undefined,
  userCode: '',
  fiscalCode: '',
  vatCodeFiscalCode: '',
  privacy: true,
  privacyAgreementRequestStatus: 'idle',
  email: '',
  insertEmailRequestStatus: 'idle',
  verificationEmailCode: '',
  verificationEmailRequestStatus: 'idle',
  phoneNumber: '',
  insertPhoneNumberRequestStatus: 'idle',
  verificationPhoneNumberCode: '',
  verificationPhoneNumberRequestStatus: 'idle',
  fiscalCodeLegalPerson: '',
  insertFiscalCodeLegalPersonRequestStatus: 'idle',
  nameUser: '',
  surnameUser: '',
  phoneNumberAttempts: 0,
  emailAttempts: 0,
  phoneNumberAlreadyExisting: false,
  signatureDocument: null,
  retrieveSignatureDocumentRequestStatus: 'idle',
  bundleId: null,
  tmpToken: null,
  insertSecondaryPhoneNumberRequestStatus: 'idle',
  prepareRegistrationRequestStatus: 'idle',
  fiduciaria: false,
};

const setupAccountSlice = createSlice({
  name: 'features/user-registration',
  initialState,
  reducers: {
    resetPrepareRegistrationRequestStatus(state) {
      state.prepareRegistrationRequestStatus = 'idle';
    },
    startRegistrationFlow(state) {
      state.flowStarted = true;
    },
    //reset state UserRegistration
    resetUserRegistrationState() {
      return initialState;
    },
    //Setup Account
    resetGetStatusRegistrazioneState(state) {
      state.getStatusRegistrazioneError = undefined;
      state.getStatusRegistrazioneRequestStatus = 'idle';
    },
    clearGetStatusRegistrazioneError(state) {
      state.getStatusRegistrazioneError = undefined;
      state.getStatusRegistrazioneRequestStatus = 'idle';
    },
    //Setup Account
    resetSetupAccountState(state) {
      state.setupAccountError = undefined;
      state.setupAccountRequestStatus = 'idle';
    },
    clearSetupAccountError(state) {
      state.setupAccountError = undefined;
      state.setupAccountRequestStatus = 'idle';
    },
    setSelectedOptionUserType(
      state,
      action: PayloadAction<PersonaTipoUtenteEnum>
    ) {
      state.selectedOptionUserType = action.payload;
    },
    setUserCodeSetupAccount(state, action: PayloadAction<string>) {
      state.userCode = action.payload;
    },
    setUserCodeSetupAccountUnchanged(state, action: PayloadAction<string>) {
      state.userCodeUnchanged = action.payload;
    },
    setFiduciaria(state, action: PayloadAction<boolean>) {
      state.fiduciaria = action.payload;
    },
    setFiscalCodeSetupAccount(state, action: PayloadAction<string>) {
      state.fiscalCode = action.payload;
    },
    setVatCodeFiscalCodeSetupAccount(state, action: PayloadAction<string>) {
      state.vatCodeFiscalCode = action.payload;
    },
    //Privacy Agreement
    resetPrivacyAgreementState(state) {
      state.privacyAgreementError = undefined;
      state.privacyAgreementRequestStatus = 'idle';
    },
    clearPrivacyAgreementError(state) {
      state.privacyAgreementError = undefined;
      state.privacyAgreementRequestStatus = 'idle';
    },
    setPrivacy(state, action: PayloadAction<boolean>) {
      state.privacy = action.payload;
    },
    //Insert Email
    resetInsertEmailRequestState(state) {
      state.insertEmailError = undefined;
      state.insertEmailRequestStatus = 'idle';
    },
    clearInsertEmailError(state) {
      state.insertEmailError = undefined;
      state.insertEmailRequestStatus = 'idle';
    },
    setEmail(state, action: PayloadAction<string>) {
      state.email = action.payload;
    },
    clearInsertEmailFields(state) {
      state.email = '';
    },
    //Verification Email
    resetVerificationEmailRequestState(state) {
      state.verificationEmailError = undefined;
      state.verificationEmailRequestStatus = 'idle';
    },
    clearVerificationEmailError(state) {
      state.verificationEmailError = undefined;
      state.verificationEmailRequestStatus = 'idle';
    },
    setVerificationEmailCode(state, action: PayloadAction<string>) {
      state.verificationEmailCode = action.payload;
    },
    clearVerificationEmailFields(state) {
      state.verificationEmailCode = '';
    },
    //Insert Phone Number
    resetInsertPhoneNumberRequestState(state) {
      state.insertPhoneNumberError = undefined;
      state.insertPhoneNumberRequestStatus = 'idle';
    },
    clearInsertPhoneNumberError(state) {
      state.insertPhoneNumberError = undefined;
      state.insertPhoneNumberRequestStatus = 'idle';
    },
    setPhoneNumber(state, action: PayloadAction<string>) {
      state.phoneNumber = action.payload;
    },
    clearInsertPhoneNumberFields(state) {
      state.phoneNumber = '';
    },
    //Insert Secondary Phone Number
    resetInsertSecondaryPhoneNumberRequestState(state) {
      state.insertSecondaryPhoneNumberError = undefined;
      state.insertSecondaryPhoneNumberRequestStatus = 'idle';
    },
    clearInsertSecondaryPhoneNumberError(state) {
      state.insertSecondaryPhoneNumberError = undefined;
      state.insertSecondaryPhoneNumberRequestStatus = 'idle';
    },
    setSecondaryPhoneNumber(state, action: PayloadAction<string>) {
      state.secondaryPhoneNumber = action.payload;
    },
    clearInsertSecondaryPhoneNumberFields(state) {
      state.secondaryPhoneNumber = '';
    },
    //Verification Phone Number
    resetVerificationPhoneNumberRequestState(state) {
      state.verificationPhoneNumberError = undefined;
      state.verificationPhoneNumberRequestStatus = 'idle';
    },
    clearVerificationPhoneNumberError(state) {
      state.verificationPhoneNumberError = undefined;
      state.verificationPhoneNumberRequestStatus = 'idle';
    },
    setVerificationPhoneNumberCode(state, action: PayloadAction<string>) {
      state.verificationPhoneNumberCode = action.payload;
    },
    clearVerificationPhoneNumberFields(state) {
      state.verificationPhoneNumberCode = '';
    },
    //Insert Fiscal Code
    resetInsertFiscalCodeLegalPersonRequestState(state) {
      state.insertFiscalCodeLegalPersonError = undefined;
      state.insertFiscalCodeLegalPersonRequestStatus = 'idle';
    },
    clearInsertFiscalCodeLegalPersonError(state) {
      state.insertFiscalCodeLegalPersonError = undefined;
      state.insertFiscalCodeLegalPersonRequestStatus = 'idle';
    },
    setInsertFiscalCodeLegalPerson(state, action: PayloadAction<string>) {
      state.fiscalCodeLegalPerson = action.payload;
    },
    clearRetrieveSignatureDocumentError(state) {
      state.retrieveSignatureDocumentError = undefined;
      state.retrieveSignatureDocumentRequestStatus = 'idle';
    },
    resetSignatureDocumentState(state) {
      state.retrieveSignatureDocumentError = undefined;
      state.retrieveSignatureDocumentRequestStatus = 'idle';
      state.signatureDocument = null;
    },
  },
  extraReducers: (builder) => {
    //Get Status Registrazione
    builder.addCase(getStatusRegistrazione.pending, (state) => {
      state.getStatusRegistrazioneRequestStatus = 'loading';
    });
    builder.addCase(getStatusRegistrazione.fulfilled, (state, action) => {
      state.getStatusRegistrazioneRequestStatus = 'succeeded';
      state.getStatusRegistrazioneResult = action.payload.status;
      state.bundleId = action.payload.bundleId;
      state.tmpToken = action.payload.temporaryToken;
      if (
        state.selectedOptionUserType !== 'PF' &&
        action.payload.status === 'BUNDLE_ATTIVO' &&
        action.payload.representativeCustomerCode
      ) {
        state.userBehalfOfCode = state.userCode;
        state.userCode = action.payload.representativeCustomerCode;
      }
    });
    builder.addCase(getStatusRegistrazione.rejected, (state, action) => {
      state.getStatusRegistrazioneError =
        (action.payload as SetupAccountError) || 'GENERIC_ERROR';
      state.getStatusRegistrazioneRequestStatus = 'failed';
    });
    //Setup Account
    builder.addCase(setupAccount.pending, (state) => {
      state.setupAccountRequestStatus = 'loading';
    });
    builder.addCase(setupAccount.fulfilled, (state, action) => {
      state.setupAccountRequestStatus = 'succeeded';
      state.nameUser = action.payload.nome;
      state.surnameUser = action.payload.cognome;
      state.userCodeBE = action.payload.codiceUtente;
    });
    builder.addCase(setupAccount.rejected, (state, action) => {
      state.setupAccountError = action.payload || 'GENERIC_ERROR';
      state.setupAccountRequestStatus = 'failed';
    });
    //Privacy Agreement
    builder.addCase(privacyAgreement.pending, (state) => {
      state.privacyAgreementRequestStatus = 'loading';
    });
    builder.addCase(privacyAgreement.fulfilled, (state) => {
      state.privacyAgreementRequestStatus = 'succeeded';
    });
    builder.addCase(privacyAgreement.rejected, (state, action) => {
      state.privacyAgreementError = action.payload || 'GENERIC_ERROR';
      state.privacyAgreementRequestStatus = 'failed';
    });
    //Insert Email
    builder.addCase(insertEmail.pending, (state) => {
      state.insertEmailRequestStatus = 'loading';
      state.verificationEmailCode = undefined;
    });
    builder.addCase(insertEmail.fulfilled, (state) => {
      state.insertEmailRequestStatus = 'succeeded';
    });
    builder.addCase(insertEmail.rejected, (state, action) => {
      state.insertEmailError = action.payload || 'GENERIC_ERROR';
      state.insertEmailRequestStatus = 'failed';
    });
    //Verification Email
    builder.addCase(verificationEmail.pending, (state) => {
      state.verificationEmailRequestStatus = 'loading';
    });
    builder.addCase(verificationEmail.fulfilled, (state, action) => {
      if (action.payload) {
        state.phoneNumberAlreadyExisting = true;
        state.phoneNumber = action.payload.cellulare;
        state.bundleId = action.payload.bundleId;
        state.tmpToken = action.payload.temporaryToken;
      } else {
        state.phoneNumberAlreadyExisting = false;
        state.phoneNumber = undefined;
      }
      state.verificationEmailRequestStatus = 'succeeded';
    });
    builder.addCase(verificationEmail.rejected, (state, action) => {
      state.verificationEmailError = action.payload;
      if (action.payload.type === 'CODICE_DI_VERIFICA_SBAGLIATO') {
        state.emailAttempts = action.payload.attempts;
      }
      state.verificationEmailRequestStatus = 'failed';
    });
    //Insert Phone Number
    builder.addCase(insertPhoneNumber.pending, (state) => {
      state.insertPhoneNumberRequestStatus = 'loading';
      state.verificationPhoneNumberCode = undefined;
    });
    builder.addCase(insertPhoneNumber.fulfilled, (state) => {
      state.insertPhoneNumberRequestStatus = 'succeeded';
    });
    builder.addCase(insertPhoneNumber.rejected, (state, action) => {
      state.insertPhoneNumberError = action.payload || 'GENERIC_ERROR';
      state.insertPhoneNumberRequestStatus = 'failed';
    });
    //Insert Secondary Phone Number
    builder.addCase(insertSecondaryPhoneNumber.pending, (state) => {
      state.insertSecondaryPhoneNumberRequestStatus = 'loading';
    });
    builder.addCase(insertSecondaryPhoneNumber.fulfilled, (state) => {
      state.insertSecondaryPhoneNumberRequestStatus = 'succeeded';
    });
    builder.addCase(insertSecondaryPhoneNumber.rejected, (state, action) => {
      state.insertSecondaryPhoneNumberError = action.payload || 'GENERIC_ERROR';
      state.insertSecondaryPhoneNumberRequestStatus = 'failed';
    });
    //Verification Phone Number
    builder.addCase(verificationPhoneNumber.pending, (state) => {
      state.verificationPhoneNumberRequestStatus = 'loading';
    });
    builder.addCase(verificationPhoneNumber.fulfilled, (state, action) => {
      state.verificationPhoneNumberRequestStatus = 'succeeded';
      state.bundleId = action.payload.bundleId;
      state.tmpToken = action.payload.temporaryToken;
    });
    builder.addCase(verificationPhoneNumber.rejected, (state, action) => {
      state.verificationPhoneNumberError = action.payload;
      if (action.payload.type === 'CODICE_DI_VERIFICA_SBAGLIATO') {
        state.emailAttempts = action.payload.attempts;
      }
      state.verificationPhoneNumberRequestStatus = 'failed';
    });
    //Insert Fiscal Code
    builder.addCase(insertFiscalCodeLegalPerson.pending, (state) => {
      state.insertFiscalCodeLegalPersonRequestStatus = 'loading';
    });
    builder.addCase(insertFiscalCodeLegalPerson.fulfilled, (state, action) => {
      state.insertFiscalCodeLegalPersonRequestStatus = 'succeeded';
      state.userBehalfOfCode = state.userCode;
      state.userCode = action.payload.codiceClienteRappresentante;
    });
    builder.addCase(insertFiscalCodeLegalPerson.rejected, (state, action) => {
      state.insertFiscalCodeLegalPersonError =
        action.payload || 'GENERIC_ERROR';
      state.insertFiscalCodeLegalPersonRequestStatus = 'failed';
    });
    // Prepare registration
    builder.addCase(prepareRegistration.pending, (state) => {
      state.prepareRegistrationRequestStatus = 'loading';
    });
    builder.addCase(prepareRegistration.fulfilled, (state) => {
      state.prepareRegistrationRequestStatus = 'succeeded';
    });
    builder.addCase(prepareRegistration.rejected, (state, action) => {
      state.prepareRegistrationError = action.payload || 'GENERIC_ERROR';
      state.prepareRegistrationRequestStatus = 'failed';
    });
    // Signature area
    builder.addCase(retrieveSignatureDocument.pending, (state) => {
      state.retrieveSignatureDocumentRequestStatus = 'loading';
    });
    builder.addCase(retrieveSignatureDocument.fulfilled, (state, action) => {
      state.retrieveSignatureDocumentRequestStatus = 'succeeded';
      state.signatureDocument = action.payload;
    });
    builder.addCase(retrieveSignatureDocument.rejected, (state, action) => {
      state.retrieveSignatureDocumentError = action.payload || 'GENERIC_ERROR';
      state.retrieveSignatureDocumentRequestStatus = 'failed';
    });
  },
});

export const {
  startRegistrationFlow,
  resetPrepareRegistrationRequestStatus,
  resetUserRegistrationState,
  resetSetupAccountState,
  setSelectedOptionUserType,
  resetGetStatusRegistrazioneState,
  clearGetStatusRegistrazioneError,
  clearSetupAccountError,
  setUserCodeSetupAccountUnchanged,
  setUserCodeSetupAccount,
  setFiscalCodeSetupAccount,
  setVatCodeFiscalCodeSetupAccount,
  resetPrivacyAgreementState,
  setPrivacy,
  clearPrivacyAgreementError,
  resetInsertEmailRequestState,
  clearInsertEmailError,
  setEmail,
  resetVerificationEmailRequestState,
  clearVerificationEmailError,
  setVerificationEmailCode,
  resetInsertPhoneNumberRequestState,
  clearInsertPhoneNumberError,
  setPhoneNumber,
  resetVerificationPhoneNumberRequestState,
  clearVerificationPhoneNumberError,
  setVerificationPhoneNumberCode,
  resetInsertFiscalCodeLegalPersonRequestState,
  clearInsertFiscalCodeLegalPersonError,
  setInsertFiscalCodeLegalPerson,
  clearVerificationEmailFields,
  clearVerificationPhoneNumberFields,
  resetSignatureDocumentState,
  clearRetrieveSignatureDocumentError,
  clearInsertEmailFields,
  clearInsertPhoneNumberFields,
  setSecondaryPhoneNumber,
  clearInsertSecondaryPhoneNumberFields,
  resetInsertSecondaryPhoneNumberRequestState,
  clearInsertSecondaryPhoneNumberError,
  setFiduciaria,
} = setupAccountSlice.actions;

export default setupAccountSlice.reducer;
